import { type FC, useCallback, type MouseEvent } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';
import { graphql, useFragment } from 'react-relay';

import { Button } from 'dibs-elements/exports/Button.tsx';
import { trackEvent, eventNameConstants, interactionTypeConstants } from 'dibs-tracking';
import {
    showCartAddedNotification,
    type CartAddedNotificationPayload,
} from 'dibs-buyer-layout/exports/cartActions.ts';
import { useAddItemToCart } from 'dibs-buyer-layout/exports/useAddItemToCart.ts';

import { type PdpSharedAddToCartButton_user$key } from './__generated__/PdpSharedAddToCartButton_user.graphql.ts';
import { type PdpSharedAddToCartButton_item$key } from './__generated__/PdpSharedAddToCartButton_item.graphql.ts';
import { type PdpSharedAddToCartButton_viewer$key } from './__generated__/PdpSharedAddToCartButton_viewer.graphql.ts';

const userFragment = graphql`
    fragment PdpSharedAddToCartButton_user on User {
        serviceId
    }
`;
const itemFragment = graphql`
    fragment PdpSharedAddToCartButton_item on Item {
        ...useAddItemToCart_item
        lastActiveTransaction(buyerId: $userId) {
            serviceId
            checkoutDate
        }
    }
`;
const viewerFragment = graphql`
    fragment PdpSharedAddToCartButton_viewer on Viewer {
        addToCartNotificationEnabled: featureFlag(feature: "AddToCartNotification")
    }
`;

export const PdpSharedAddToCartButton: FC<{
    user: PdpSharedAddToCartButton_user$key | null | undefined;
    item: PdpSharedAddToCartButton_item$key;
    viewer: PdpSharedAddToCartButton_viewer$key | null | undefined;
    skuId?: string;
    quantitySelected?: string;
}> = ({ user: userRef, item: itemRef, viewer: viewerRef, skuId, quantitySelected }) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const user = useFragment(userFragment, userRef);
    const userId = user?.serviceId || null;
    const item = useFragment(itemFragment, itemRef);
    const viewer = useFragment(viewerFragment, viewerRef);

    const transaction = item?.lastActiveTransaction;
    const { serviceId: transactionId, checkoutDate } = transaction || {};
    const shouldRedirectToOrder = !!(transactionId && checkoutDate);

    const onClick: (event?: MouseEvent<HTMLElement>) => void = useCallback(
        event => {
            trackEvent(
                {
                    action: 'pdp add to cart button',
                    category: 'add to cart click',
                    eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
                    interaction_type: interactionTypeConstants.INTERACTION_TYPE_ADD_TO_CART,
                },
                event
            );
            if (shouldRedirectToOrder) {
                window.location.assign(
                    handleLocaleUrl(`/orders/order/${transactionId}/`, intl.locale)
                );
            }
        },
        [intl.locale, transactionId, shouldRedirectToOrder]
    );

    const onAddedToCart = useCallback(
        (payload: CartAddedNotificationPayload) => {
            if (shouldRedirectToOrder) {
                return;
            }
            if (viewer?.addToCartNotificationEnabled) {
                dispatch(showCartAddedNotification(payload));
                return;
            }
            window.location.assign(handleLocaleUrl('/my/cart/', intl.locale));
        },
        [dispatch, shouldRedirectToOrder, viewer?.addToCartNotificationEnabled, intl.locale]
    );

    const { addItemToCart, isAddingItemToCart } = useAddItemToCart({
        quantitySelected: quantitySelected || '1',
        onClick,
        onAddedToCart,
        item,
        userId,
        skuId: skuId || null,
    });

    return (
        <Button
            fullWidth
            type="secondary"
            size="large"
            onClick={e => addItemToCart({ event: e, type: 'CHECKOUT' })}
            disabled={isAddingItemToCart}
            dataTn="pdp-shared-add-to-cart-button"
        >
            <FormattedMessage
                id="PdpSharedAddToCartButton.addToCart"
                defaultMessage="Add to cart"
            />
        </Button>
    );
};
